import { camelCase, cloneDeep, unescape } from "lodash";
import PROFILE_SECTIONS from "PFApp/profiles/profile_sections";
import { getProfileName } from "PFCore/helpers/profile";
import { useCategorizedSkills } from "PFCore/helpers/use_categorized_skills";
import i18n from "PFCore/i18n";

export default class ExportableProfiles {
  constructor(data, customTypes) {
    this.customTypes = customTypes;
    this.originalData = cloneDeep(data);
    this.data = cloneDeep(data).map((it) => new ExportableProfile(it, customTypes));
  }

  get(index) {
    return this.data[index];
  }

  get length() {
    return this.data.length;
  }

  map(fn) {
    return this.data.map(fn);
  }

  filterEnabled() {
    return this.data.filter(({ enabled }) => enabled !== false);
  }

  countEnabled() {
    return this.filterEnabled().length;
  }
}

class ExportableProfile {
  constructor(profile, customTypes) {
    this.customTypes = customTypes;
    this.profile = profile;
    this.positions = this.profile.positions?.map((it) => this.calcPosition(it)) || [];
    this._originalPositions = this.profile.positions?.map((it) => this.calcPosition(it)) || [];
    this.score = profile.score; // when exported from matches and shortlist (but not from show page)
    this.mainOriginalCompany = unescape(this.positions.find((it) => it.main)?.companyName);
    this.mainOriginalJobTitle = unescape(this.positions.find((it) => it.main)?.jobTitle);
    this.originalSummary = unescape(this.profile.summary);
    this.resetNames();

    this.customFields = customTypes
      .map((type) => {
        if (!profile[type.name]) {
          return { type, value: "" };
        }

        return {
          type,
          value: this.getOriginalValueOfCustomField(type)
        };
      })
      .filter(Boolean);

    this.resetSummary();
    this.resetMainJobTitle();
    this.resetMainCompany();
  }

  serialize(outsideData = {}) {
    const out = {
      jobTitle: this.mainJobTitle,
      companyName: this.mainCompany,

      // for building the filename
      first_name: this.profile.first_name || "",
      last_name: this.profile.last_name || "",
      avatar: this.profile.avatar, // this will be overwritten by the exporter
      id: this.profile.id,
      name: getProfileName({ firstName: this.firstName, lastName: this.lastName }),
      email: this.profile.email,

      // cannot edit for now:
      score: this.score,
      contactInformationItems: this.profile.contact_information.map(({ type, value, label }) => ({
        type: `${i18n.t(`documentButtons.contactOptions.${camelCase(type)}`, {
          defaultValue: type
        })}${label ? ` (${label})` : ""}`,
        text: value
      })),
      _originalData: this.profile,
      ...outsideData
    };

    const filteredPositions = this.positions?.filter(({ enabled }) => enabled);
    if (filteredPositions?.length > 0) {
      out.positions = { header: "Positions", items: filteredPositions };
    }

    if (this.summary) {
      out.summary = this.summary;
    }

    out.customTypes = this.customTypes
      .map((type) => {
        const val = this.getCustomFieldValue(type.name);

        return {
          type,
          displayAs: type.display_as,
          text: val,
          rawText: val
        };
      })
      .filter(Boolean);

    return out;
  }

  getCustomTypeByName(typeName) {
    return this.customTypes.find(({ name }) => name === typeName);
  }

  getCustomTypeBySection(sectionName) {
    return this.customTypes.find(({ sections }) => sections.find(({ key }) => key === sectionName));
  }

  getFirstValueBySection(sectionName) {
    const type = this.getCustomTypeBySection(sectionName);
    return this.profile[type.name]?.[0]?.text || "";
  }

  setFirstName(val) {
    this.firstName = val;
  }

  setLastName(val) {
    this.lastName = val;
  }

  resetNames() {
    this.firstName = this.profile.first_name;
    this.lastName = this.profile.last_name;
  }

  hasChangedNames() {
    return this.firstName !== this.profile.first_name || this.lastName !== this.profile.last_name;
  }

  // main job title
  setMainJobTitle(val) {
    this.mainJobTitle = val;
  }

  resetMainJobTitle() {
    this.mainJobTitle = this.mainOriginalJobTitle;
  }

  hasChangedMainJobTitle() {
    return this.mainJobTitle !== this.mainOriginalJobTitle;
  }

  setMainCompany(val) {
    this.mainCompany = val;
  }

  resetMainCompany() {
    this.mainCompany = this.mainOriginalCompany;
  }

  hasChangedCompany() {
    return this.mainCompany !== this.mainOriginalCompany;
  }

  // summary
  setSummary(val) {
    this.summary = val;
  }

  resetSummary() {
    this.summary = this.originalSummary;
  }

  hasChangedSummary() {
    return this.summary !== this.originalSummary;
  }

  // custom field
  getCustomField(typeName) {
    return this.customFields.find(({ type }) => type.name === typeName);
  }

  getRawCustomField(typeName) {
    return this.profile[typeName];
  }

  updateCustomFieldValue(typeName, value) {
    this.getCustomField(typeName).value = value;
  }

  getCustomFieldValue(typeName) {
    return this.getCustomField(typeName)?.value || "";
  }

  getOriginalValueOfCustomField(type) {
    try {
      const vals = this.profile[type.name].map(({ text }) => text);
      if (type.value_type === "date") {
        return vals.map((date) => moment(date).format("LL")).join("\r ");
      } else {
        return vals.join("\r ");
      }
    } catch {
      return "";
    }
  }

  resetCustomFieldValue(typeName) {
    const type = this.getCustomTypeByName(typeName);
    this.getCustomField(typeName).value = this.getOriginalValueOfCustomField(type);
  }

  hasChangedCustomFieldValue(typeName) {
    const type = this.getCustomTypeByName(typeName);
    return this.getCustomField(typeName).value !== this.getOriginalValueOfCustomField(type);
  }

  // skills
  getCategorisedSkills() {
    // eslint-disable-next-line
    const [sortedSkills, otherSkills] = useCategorizedSkills(
      this.profile.skills,
      this.profile.skill_categories
    );

    return [sortedSkills, otherSkills];
  }

  setUnselectedSkills(val) {
    this.unselected = val;
  }

  getUnselectedSkills() {
    return this.unselected || [];
  }

  // positions
  calcPosition(pos) {
    const jobType = this.getCustomTypeBySection(
      pos.main ? PROFILE_SECTIONS.current_experience_title_1 : PROFILE_SECTIONS.past_experience_title_1
    );

    const jobTitle = unescape(pos[jobType.name]?.[0]?.text);

    const companyType = this.getCustomTypeBySection(
      pos.main ? PROFILE_SECTIONS.current_experience_title_2 : PROFILE_SECTIONS.past_experience_title_2
    );
    const companyName = unescape(pos[companyType?.name]?.[0]?.text);

    return {
      enabled: true,
      id: pos.id,
      main: !!pos.main,
      jobTitle,
      companyName,
      current: !!pos.current,
      startDate: pos.start_date ? new Date(pos.start_date).toISOString() : "",
      endDate: pos.end_date ? new Date(pos.end_date).toISOString() : "",
      summary: unescape(pos.summary),
      _originalPosition: pos
    };
  }

  updatePosition(posId, data) {
    const pos = this.positions.find(({ id }) => id === posId);
    data && Object.assign(pos, data);
  }

  resetPosition(posId) {
    const pos = this.positions.find(({ id }) => id === posId);
    const originalPos = this._originalPositions.find(({ id }) => id === posId);
    Object.assign(pos, { ...originalPos, enabled: pos.enabled });
  }

  hasChangedPosition(posId) {
    const pos = this.positions.find(({ id }) => id === posId);
    const originalPos = this._originalPositions.find(({ id }) => id === posId);
    return JSON.stringify({ ...pos, enabled: originalPos.enabled }) !== JSON.stringify(originalPos);
  }
}
